import { useMemo, createElement } from 'react';
import { useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import CampaignIcon from '@mui/icons-material/Campaign';
import InfoIcon from '@mui/icons-material/Info';

import { BreadcrumbItem } from './Breadcrumbs';

interface BreadcrumbDataContext {
  groupName?: string;
  groupId?: string;
  meetingTitle?: string;
  meetingId?: string;
}

export const useBreadcrumbs = (context?: BreadcrumbDataContext): BreadcrumbItem[] => {
  const location = useLocation();

  return useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Always start with Home for non-root paths
    if (location.pathname !== '/') {
      breadcrumbs.push({
        label: 'Home',
        path: '/',
        icon: createElement(HomeIcon, { fontSize: 'small' }),
      });
    }

    // Build breadcrumbs based on path segments
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      const pathUpToSegment = '/' + pathSegments.slice(0, i + 1).join('/');
      const isLast = i === pathSegments.length - 1;

      switch (segment) {
        case 'groups':
          if (isLast) {
            // On groups listing page
            breadcrumbs.push({
              label: 'Groups',
              icon: createElement(GroupsIcon, { fontSize: 'small' }),
            });
          } else {
            // Groups as intermediate path
            breadcrumbs.push({
              label: 'Groups',
              path: '/groups',
              icon: createElement(GroupsIcon, { fontSize: 'small' }),
            });
          }
          break;

        case 'meetings':
          if (isLast) {
            // On meetings listing page (if it exists)
            breadcrumbs.push({
              label: 'Events',
              icon: createElement(EventIcon, { fontSize: 'small' }),
            });
          } else {
            // Meetings as intermediate path
            breadcrumbs.push({
              label: 'Events',
              path: '/meetings',
              icon: createElement(EventIcon, { fontSize: 'small' }),
            });
          }
          break;

        case 'profile':
          breadcrumbs.push({
            label: 'Profile',
            icon: createElement(PersonIcon, { fontSize: 'small' }),
          });
          break;

        case 'broadcast':
          breadcrumbs.push({
            label: 'Broadcast',
            icon: createElement(CampaignIcon, { fontSize: 'small' }),
          });
          break;

        case 'about':
          breadcrumbs.push({
            label: 'About',
            icon: createElement(InfoIcon, { fontSize: 'small' }),
          });
          break;

        default:
          // Handle dynamic segments like groupId, meetingId
          if (i > 0) {
            const previousSegment = pathSegments[i - 1];

            if (previousSegment === 'groups' && context?.groupName) {
              // Group detail page
              if (isLast) {
                breadcrumbs.push({
                  label: context.groupName,
                  truncate: true,
                });
              } else {
                breadcrumbs.push({
                  label: context.groupName,
                  path: pathUpToSegment,
                  truncate: true,
                });
              }
            } else if (previousSegment === 'meetings' && context?.meetingTitle) {
              // Meeting detail page
              breadcrumbs.push({
                label: context.meetingTitle,
                truncate: true,
              });
            } else if (segment) {
              // Generic handling for unknown dynamic segments
              const capitalizedSegment =
                segment.charAt(0).toUpperCase() + segment.slice(1);
              if (isLast) {
                breadcrumbs.push({
                  label: capitalizedSegment,
                  truncate: true,
                });
              } else {
                breadcrumbs.push({
                  label: capitalizedSegment,
                  path: pathUpToSegment,
                  truncate: true,
                });
              }
            }
          }
          break;
      }
    }

    return breadcrumbs;
  }, [location.pathname, context]);
};

// Helper hook for group detail pages
export const useGroupBreadcrumbs = (
  groupName?: string,
  groupId?: string,
): BreadcrumbItem[] => {
  return useBreadcrumbs({
    groupName,
    groupId,
  });
};

// Helper hook for meeting detail pages
export const useMeetingBreadcrumbs = (
  meetingTitle?: string,
  meetingId?: string,
  groupName?: string,
  groupId?: string,
): BreadcrumbItem[] => {
  const location = useLocation();

  return useMemo(() => {
    if (!meetingTitle) {
      return [];
    }

    // For meeting details, we want: Home > Groups > [Group Name] > [Meeting Title]
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Home',
        path: '/',
        icon: createElement(HomeIcon, { fontSize: 'small' }),
      },
      {
        label: 'Groups',
        path: '/groups',
        icon: createElement(GroupsIcon, { fontSize: 'small' }),
      },
    ];

    // Add group breadcrumb if we have group info
    if (groupName && groupId) {
      breadcrumbs.push({
        label: groupName,
        path: `/groups/${groupId}`,
        truncate: true,
      });
    }

    // Add current meeting
    breadcrumbs.push({
      label: meetingTitle,
      truncate: true,
    });

    return breadcrumbs;
  }, [meetingTitle, groupName, groupId]);
};

export default useBreadcrumbs;
